import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Upload, AlertTriangle, FileText, Calendar, Search, Filter, Download, RefreshCw, Clock } from 'lucide-react';

interface BudgetItem {
  id: string;
  category: string;
  description: string;
  budgetedAmount: number;
  actualAmount: number;
  commitments: number;
  remaining: number;
  variance: number;
  variancePercentage: number;
}

interface ProjectBudget {
  id: string;
  projectName: string;
  totalBudget: number;
  totalActual: number;
  totalCommitments: number;
  totalRemaining: number;
  startDate: string;
  endDate: string;
  items: BudgetItem[];
}

export function BudgetMonitoring() {
  const [selectedProject, setSelectedProject] = useState<string>('1');
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [varianceFilter, setVarianceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('category');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const projects: ProjectBudget[] = [
    {
      id: '1',
      projectName: 'Residential Complex - Phase 1',
      totalBudget: 2500000,
      totalActual: 950000,
      totalCommitments: 600000,
      totalRemaining: 950000,
      startDate: '2024-01-15',
      endDate: '2024-12-31',
      items: [
        {
          id: '1',
          category: 'Site Preparation',
          description: 'Excavation, Leveling, Site Setup',
          budgetedAmount: 200000,
          actualAmount: 185000,
          commitments: 0,
          remaining: 15000,
          variance: -15000,
          variancePercentage: -7.5
        },
        {
          id: '2',
          category: 'Concrete Works',
          description: 'Foundation, Columns, Beams, Slabs',
          budgetedAmount: 800000,
          actualAmount: 420000,
          commitments: 300000,
          remaining: 80000,
          variance: 80000,
          variancePercentage: 10
        },
        {
          id: '3',
          category: 'Steel Works',
          description: 'Reinforcement Bars, Structural Steel',
          budgetedAmount: 600000,
          actualAmount: 345000,
          commitments: 200000,
          remaining: 55000,
          variance: 55000,
          variancePercentage: 9.17
        },
        {
          id: '4',
          category: 'Masonry Works',
          description: 'Brick Work, Block Work',
          budgetedAmount: 300000,
          actualAmount: 0,
          commitments: 100000,
          remaining: 200000,
          variance: 200000,
          variancePercentage: 66.67
        },
        {
          id: '5',
          category: 'MEP Works',
          description: 'Electrical, Plumbing, HVAC',
          budgetedAmount: 400000,
          actualAmount: 0,
          commitments: 0,
          remaining: 400000,
          variance: 400000,
          variancePercentage: 100
        },
        {
          id: '6',
          category: 'Finishing Works',
          description: 'Flooring, Painting, Fixtures',
          budgetedAmount: 200000,
          actualAmount: 0,
          commitments: 0,
          remaining: 200000,
          variance: 200000,
          variancePercentage: 100
        }
      ]
    }
  ];

  const currentProject = projects.find(p => p.id === selectedProject);

  const monthlySpendData = [
    { month: 'Jan', budgeted: 200000, actual: 185000, forecast: 190000 },
    { month: 'Feb', budgeted: 400000, actual: 420000, forecast: 410000 },
    { month: 'Mar', budgeted: 350000, actual: 345000, forecast: 340000 },
    { month: 'Apr', budgeted: 300000, actual: 0, forecast: 310000 },
    { month: 'May', budgeted: 250000, actual: 0, forecast: 260000 },
    { month: 'Jun', budgeted: 200000, actual: 0, forecast: 200000 },
  ];

  const categorySpendData = currentProject?.items.map(item => ({
    name: item.category,
    budgeted: item.budgetedAmount,
    actual: item.actualAmount,
    remaining: item.remaining
  })) || [];

  // Enhanced pie chart data with percentages
  const calculatePercentage = (value: number, total: number) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
  };

  const totalBudget = currentProject?.totalBudget || 0;
  const pieChartData = [
    { 
      name: 'Actual Spent', 
      value: currentProject?.totalActual || 0, 
      color: '#ef4444',
      percentage: calculatePercentage(currentProject?.totalActual || 0, totalBudget)
    },
    { 
      name: 'Commitments', 
      value: currentProject?.totalCommitments || 0, 
      color: '#f59e0b',
      percentage: calculatePercentage(currentProject?.totalCommitments || 0, totalBudget)
    },
    { 
      name: 'Remaining', 
      value: currentProject?.totalRemaining || 0, 
      color: '#10b981',
      percentage: calculatePercentage(currentProject?.totalRemaining || 0, totalBudget)
    }
  ];

  // Custom label function for pie chart
  const renderCustomLabel = (entry: any) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent } = entry;
    if (percent < 0.05) return null; // Don't show labels for slices smaller than 5%
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  // Custom tooltip for enhanced information
  const customTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            Value: {formatCurrency(data.value)}
          </p>
          <p className="text-sm text-muted-foreground">
            Percentage: {data.percentage}%
          </p>
        </div>
      );
    }
    return null;
  };

  // Filter and search logic for budget items
  const filteredBudgetItems = useMemo(() => {
    if (!currentProject) return [];
    
    let filtered = currentProject.items.filter(item => {
      const matchesSearch = item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || 
                           (statusFilter === 'overspent' && item.variancePercentage < -5) ||
                           (statusFilter === 'warning' && item.variancePercentage >= -5 && item.variancePercentage < 0) ||
                           (statusFilter === 'on-track' && item.variancePercentage >= 0 && item.variancePercentage <= 10) ||
                           (statusFilter === 'under-budget' && item.variancePercentage > 10);
      
      const matchesVariance = varianceFilter === 'all' ||
                             (varianceFilter === 'positive' && item.variance > 0) ||
                             (varianceFilter === 'negative' && item.variance < 0) ||
                             (varianceFilter === 'zero' && item.variance === 0);
      
      return matchesSearch && matchesStatus && matchesVariance;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'category':
          aValue = a.category;
          bValue = b.category;
          break;
        case 'budgeted':
          aValue = a.budgetedAmount;
          bValue = b.budgetedAmount;
          break;
        case 'actual':
          aValue = a.actualAmount;
          bValue = b.actualAmount;
          break;
        case 'variance':
          aValue = a.variance;
          bValue = b.variance;
          break;
        case 'percentage':
          aValue = a.variancePercentage;
          bValue = b.variancePercentage;
          break;
        default:
          aValue = a.category;
          bValue = b.category;
      }
      
      if (typeof aValue === 'string') {
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

    return filtered;
  }, [currentProject, searchTerm, statusFilter, varianceFilter, sortBy, sortOrder]);

  const getVarianceBadge = (variance: number, percentage: number) => {
    if (percentage < -10) {
      return <Badge variant="destructive">Over Budget</Badge>;
    } else if (percentage < -5) {
      return <Badge className="bg-orange-100 text-orange-800 border-orange-200">Warning</Badge>;
    } else if (percentage > 10) {
      return <Badge className="bg-green-100 text-green-800 border-green-200">Under Budget</Badge>;
    } else {
      return <Badge variant="outline">On Track</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setVarianceFilter('all');
    setSortBy('category');
    setSortOrder('asc');
  };

  if (!currentProject) return <div>No project selected</div>;

  const overallProgress = ((currentProject.totalActual + currentProject.totalCommitments) / currentProject.totalBudget) * 100;
  const spentPercentage = (currentProject.totalActual / currentProject.totalBudget) * 100;

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div className="space-y-1">
          <h2 className="text-3xl font-semibold tracking-tight">Budget & Cost Control</h2>
          <p className="text-muted-foreground">
            Real-time financial monitoring with comprehensive budget analysis for {currentProject.projectName}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload Budget
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] modal-content">
              <DialogHeader>
                <DialogTitle>Upload Approved Budget</DialogTitle>
                <DialogDescription>Upload the approved budget document for this project</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="budget-file">Budget File</Label>
                  <Input id="budget-file" type="file" accept=".xlsx,.xls,.csv,.pdf" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="budget-version">Version</Label>
                  <Input id="budget-version" placeholder="e.g., v2.1" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="approval-date">Approval Date</Label>
                  <Input id="approval-date" type="date" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsUploadOpen(false)}>Cancel</Button>
                <Button onClick={() => setIsUploadOpen(false)}>Upload Budget</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Generate Report
          </Button>
          <Button variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Data
          </Button>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-blue-600" />
              Total Budget
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{formatCurrency(currentProject.totalBudget)}</div>
            <p className="text-xs text-blue-600 mt-1">Approved allocation</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-red-600" />
              Actual Spent
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{formatCurrency(currentProject.totalActual)}</div>
            <p className="text-xs text-red-600 mt-1">{spentPercentage.toFixed(1)}% of total budget</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-600" />
              Commitments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-700">{formatCurrency(currentProject.totalCommitments)}</div>
            <p className="text-xs text-orange-600 mt-1">Approved but unspent</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-green-600" />
              Available
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{formatCurrency(currentProject.totalRemaining)}</div>
            <p className="text-xs text-green-600 mt-1">Remaining budget</p>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-primary" />
              Budget Progress
            </CardTitle>
            <CardDescription>Overall budget utilization tracking</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium">Overall Progress</span>
                <span className="font-semibold">{overallProgress.toFixed(1)}%</span>
              </div>
              <Progress value={overallProgress} className="h-3" />
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-sm font-medium">Spent</span>
                </div>
                <span className="text-sm font-semibold">{formatCurrency(currentProject.totalActual)}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                  <span className="text-sm font-medium">Committed</span>
                </div>
                <span className="text-sm font-semibold">{formatCurrency(currentProject.totalCommitments)}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium">Available</span>
                </div>
                <span className="text-sm font-semibold">{formatCurrency(currentProject.totalRemaining)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Budget Distribution
            </CardTitle>
            <CardDescription>Financial allocation breakdown with detailed percentages</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={customTooltip} />
                  <Legend 
                    formatter={(value, entry: any) => `${value}: ${entry.payload?.percentage}%`}
                    wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              
              <div className="space-y-4">
                <h4 className="font-semibold">Financial Breakdown</h4>
                {pieChartData.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <span className="font-medium">{item.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{formatCurrency(item.value)}</div>
                      <div className="text-sm text-muted-foreground">{item.percentage}%</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="categories" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="categories">Budget Categories</TabsTrigger>
          <TabsTrigger value="trends">Spending Trends</TabsTrigger>
          <TabsTrigger value="variances">Variance Analysis</TabsTrigger>
          <TabsTrigger value="payments">Payment Tracking</TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="space-y-6">
          {/* Enhanced Search and Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Search & Filter Budget Categories
              </CardTitle>
              <CardDescription>Find and filter budget categories by various criteria</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="search">Search Categories</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      id="search"
                      placeholder="Search by category or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="status-filter">Budget Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="overspent">Over Budget</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="on-track">On Track</SelectItem>
                      <SelectItem value="under-budget">Under Budget</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="variance-filter">Variance Type</Label>
                  <Select value={varianceFilter} onValueChange={setVarianceFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Variances</SelectItem>
                      <SelectItem value="positive">Positive (Under Budget)</SelectItem>
                      <SelectItem value="negative">Negative (Over Budget)</SelectItem>
                      <SelectItem value="zero">No Variance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="sort-by">Sort By</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="category">Category</SelectItem>
                      <SelectItem value="budgeted">Budgeted Amount</SelectItem>
                      <SelectItem value="actual">Actual Amount</SelectItem>
                      <SelectItem value="variance">Variance</SelectItem>
                      <SelectItem value="percentage">Variance %</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="sort-order">Sort Order</Label>
                  <div className="flex gap-2">
                    <Select value={sortOrder} onValueChange={(value: 'asc' | 'desc') => setSortOrder(value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">Ascending</SelectItem>
                        <SelectItem value="desc">Descending</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={resetFilters} title="Reset Filters">
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {filteredBudgetItems.length} of {currentProject.items.length} categories
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export Filtered
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Enhanced Budget Categories Table */}
          <Card>
            <CardHeader>
              <CardTitle>Budget Categories</CardTitle>
              <CardDescription>
                Detailed breakdown of budget allocation and spending across all project categories
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Budgeted</TableHead>
                      <TableHead className="text-right">Actual</TableHead>
                      <TableHead className="text-right">Commitments</TableHead>
                      <TableHead className="text-right">Remaining</TableHead>
                      <TableHead className="text-right">Variance</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBudgetItems.map((item) => (
                      <TableRow key={item.id} className="hover:bg-muted/50">
                        <TableCell>
                          <div>
                            <p className="font-medium">{item.category}</p>
                            <p className="text-sm text-muted-foreground">{item.description}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(item.budgetedAmount)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.actualAmount)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.commitments)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.remaining)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {item.variance > 0 ? (
                              <TrendingDown className="w-4 h-4 text-green-600" />
                            ) : item.variance < 0 ? (
                              <TrendingUp className="w-4 h-4 text-red-600" />
                            ) : null}
                            <div className="text-right">
                              <span className={item.variance > 0 ? 'text-green-600 font-medium' : item.variance < 0 ? 'text-red-600 font-medium' : 'text-muted-foreground'}>
                                {formatCurrency(Math.abs(item.variance))}
                              </span>
                              <div className="text-xs text-muted-foreground">
                                {item.variancePercentage.toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{getVarianceBadge(item.variance, item.variancePercentage)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {filteredBudgetItems.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Filter className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No budget categories match your current filters</p>
                    <Button variant="outline" size="sm" onClick={resetFilters} className="mt-2">
                      Clear Filters
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Spending Trends</CardTitle>
              <CardDescription>Budgeted vs actual spending over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlySpendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  <Line type="monotone" dataKey="budgeted" stroke="#3b82f6" strokeWidth={2} name="Budgeted" />
                  <Line type="monotone" dataKey="actual" stroke="#ef4444" strokeWidth={2} name="Actual" />
                  <Line type="monotone" dataKey="forecast" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" name="Forecast" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Category Spending Comparison</CardTitle>
              <CardDescription>Budgeted vs actual spending by category</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categorySpendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  <Bar dataKey="budgeted" fill="#3b82f6" name="Budgeted" />
                  <Bar dataKey="actual" fill="#ef4444" name="Actual" />
                  <Bar dataKey="remaining" fill="#10b981" name="Remaining" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="variances" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Variance Analysis</CardTitle>
              <CardDescription>Detailed analysis of budget variances</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {currentProject.items
                  .filter(item => Math.abs(item.variancePercentage) > 5)
                  .map((item) => (
                    <div key={item.id} className={`border rounded p-4 ${
                      item.variancePercentage < -10 ? 'border-red-200 bg-red-50' : 
                      item.variancePercentage < -5 ? 'border-orange-200 bg-orange-50' : 
                      'border-green-200 bg-green-50'
                    }`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-medium">{item.category}</h4>
                          <p className="text-sm text-muted-foreground">{item.description}</p>
                        </div>
                        {getVarianceBadge(item.variance, item.variancePercentage)}
                      </div>
                      
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Budgeted</p>
                          <p className="font-medium">{formatCurrency(item.budgetedAmount)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Actual</p>
                          <p className="font-medium">{formatCurrency(item.actualAmount)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Variance</p>
                          <p className={`font-medium ${item.variance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(item.variance)} ({item.variancePercentage.toFixed(1)}%)
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Impact</p>
                          <p className="font-medium">
                            {item.variancePercentage < -10 ? 'Critical' : 
                             item.variancePercentage < -5 ? 'High' : 'Positive'}
                          </p>
                        </div>
                      </div>
                      
                      {item.variancePercentage < -5 && (
                        <div className="mt-3 p-2 bg-white rounded border-l-4 border-orange-400">
                          <p className="text-sm">
                            <AlertTriangle className="w-4 h-4 inline mr-1" />
                            <strong>Action Required:</strong> Review spending in this category and implement cost control measures.
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Payment Tracking</CardTitle>
              <CardDescription>Track payments and pending invoices</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Pending Invoices</p>
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(150000)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Payments Made</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(currentProject.totalActual)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Outstanding</p>
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(25000)}</p>
                  </div>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">INV-2024-101</TableCell>
                      <TableCell>ABC Concrete Ltd.</TableCell>
                      <TableCell>Concrete Works</TableCell>
                      <TableCell>{formatCurrency(137500)}</TableCell>
                      <TableCell>2024-01-25</TableCell>
                      <TableCell><Badge className="bg-green-100 text-green-800 border-green-200">Paid</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">INV-2024-102</TableCell>
                      <TableCell>Steel Works Inc.</TableCell>
                      <TableCell>Steel Works</TableCell>
                      <TableCell>{formatCurrency(325000)}</TableCell>
                      <TableCell>2024-01-30</TableCell>
                      <TableCell><Badge className="bg-orange-100 text-orange-800 border-orange-200">Pending</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">INV-2024-103</TableCell>
                      <TableCell>Equipment Rental Co.</TableCell>
                      <TableCell>Site Preparation</TableCell>
                      <TableCell>{formatCurrency(47500)}</TableCell>
                      <TableCell>2024-02-05</TableCell>
                      <TableCell><Badge className="bg-blue-100 text-blue-800 border-blue-200">Approved</Badge></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}